import React, { createContext, useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./Navbar";

/* Layout delle pagine legali di prodotto (stesso stile di VeyroPrivacy/VeyroTerms).
   `accent` è un colore hex, `rgb` la stessa tinta come "r,g,b" per le trasparenze. */
const Theme = createContext({ accent: "#a78bfa", rgb: "167,139,250" });

export const legalUl = {
  paddingLeft: "1.25rem",
  margin: "0.75rem 0",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

export function useLegalLink() {
  const { accent } = useContext(Theme);
  return { color: accent, textDecoration: "underline", textUnderlineOffset: "3px" };
}

export function Strong({ children }) {
  return <strong style={{ color: "var(--color-text)" }}>{children}</strong>;
}

export function Sub({ children }) {
  return (
    <h3
      style={{
        fontFamily: "var(--font-display)",
        fontWeight: 600,
        fontSize: "1.05rem",
        color: "var(--color-text)",
        margin: "1.5rem 0 0.5rem",
      }}
    >
      {children}
    </h3>
  );
}

export function Callout({ children }) {
  const { rgb } = useContext(Theme);
  return (
    <div
      style={{
        background: `rgba(${rgb},0.06)`,
        border: `1px solid rgba(${rgb},0.22)`,
        borderRadius: "var(--radius-lg)",
        padding: "1.25rem 1.5rem",
        margin: "0.5rem 0 1rem",
        color: "var(--color-text-2)",
        lineHeight: 1.8,
      }}
    >
      {children}
    </div>
  );
}

export function Section({ n, title, children }) {
  const { accent, rgb } = useContext(Theme);
  return (
    <div
      style={{
        marginBottom: "2.75rem",
        paddingBottom: "2.75rem",
        borderBottom: "1px solid var(--color-border)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "1rem" }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: "0.7rem",
            fontWeight: 500,
            color: accent,
            background: `rgba(${rgb},0.1)`,
            border: `1px solid rgba(${rgb},0.28)`,
            borderRadius: "6px",
            padding: "3px 9px",
            flexShrink: 0,
          }}
        >
          {String(n).padStart(2, "0")}
        </span>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontWeight: 700,
            fontSize: "clamp(1.3rem, 2.5vw, 1.6rem)",
            color: "var(--color-text)",
            letterSpacing: "-0.01em",
            margin: 0,
          }}
        >
          {title}
        </h2>
      </div>
      <div style={{ color: "var(--color-text-2)", fontSize: "1rem", lineHeight: 1.85 }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Pagina legale completa: Navbar, hero, intro, sezioni, footer con link app/privacy/termini.
 * Props: accent, rgb, gradient, appName, base (es. "/icoach"), eyebrow, title, highlight, meta, intro.
 */
export default function LegalLayout({
  accent, rgb, gradient, appName, base, eyebrow, title, highlight, meta, intro, children,
}) {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const footerLink = (to, label) => (
    <Link
      to={to}
      style={{
        fontSize: "0.9rem",
        color: pathname === to ? accent : "var(--color-muted)",
        textDecoration: "none",
        transition: "color 0.3s",
      }}
    >
      {label}
    </Link>
  );

  return (
    <Theme.Provider value={{ accent, rgb }}>
      <Navbar />
      <motion.main
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.4 }}
        style={{ background: "var(--color-bg)", minHeight: "100vh" }}
      >
        <section
          style={{
            paddingTop: "68px",
            background: `linear-gradient(180deg, rgba(${rgb},0.08) 0%, transparent 100%)`,
            borderBottom: "1px solid var(--color-border)",
          }}
        >
          <div className="container" style={{ padding: "4rem 1.75rem 3rem" }}>
            <span
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "0.82rem",
                color: accent,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                display: "block",
                marginBottom: "1rem",
              }}
            >
              {eyebrow}
            </span>
            <h1
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 800,
                fontSize: "clamp(2.2rem, 5vw, 3.2rem)",
                letterSpacing: "-0.03em",
                lineHeight: 1.1,
                marginBottom: "1rem",
              }}
            >
              {title}{" "}
              <span
                style={{
                  background: gradient,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                }}
              >
                {highlight}
              </span>
            </h1>
            <p style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--color-muted)" }}>
              {meta}
            </p>
          </div>
        </section>

        <div className="container" style={{ padding: "3rem 1.75rem 5rem" }}>
          <div
            style={{
              background: `rgba(${rgb},0.06)`,
              border: `1px solid rgba(${rgb},0.22)`,
              backdropFilter: "blur(12px)",
              borderRadius: "var(--radius-lg)",
              padding: "1.5rem 1.75rem",
              marginBottom: "3rem",
              color: "var(--color-text-2)",
              fontSize: "1rem",
              lineHeight: 1.8,
            }}
          >
            {intro}
          </div>
          {children}
        </div>

        <footer style={{ borderTop: "1px solid var(--color-border)", padding: "2rem 0" }}>
          <div
            className="container"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "1rem",
            }}
          >
            <Link
              to={base}
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "var(--color-text)",
                textDecoration: "none",
              }}
            >
              ← {appName}
            </Link>
            <div style={{ display: "flex", gap: "1.5rem" }}>
              {footerLink(`${base}/policy`, "Privacy")}
              {footerLink(`${base}/terms`, "Termini")}
            </div>
            <span style={{ fontFamily: "var(--font-mono)", fontSize: "0.82rem", color: "var(--color-muted)" }}>
              © {new Date().getFullYear()} VMDProject
            </span>
          </div>
        </footer>
      </motion.main>
    </Theme.Provider>
  );
}
